import { cn } from "@/lib/cn";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

type TooltipWrapperProps = {
  /** ツールチップをトリガーする要素 */
  children: ReactNode;
  /** ツールチップの内容（空の場合はツールチップを表示しない） */
  content?: ReactNode;
  /** ツールチップのカスタムクラス名 */
  contentClassName?: string;
  /** ツールチップの表示位置 */
  side?: "top" | "right" | "bottom" | "left";
  /** ツールチップと要素の間の距離（px） */
  sideOffset?: number;
  /** ツールチップの表示までの遅延時間（ms） */
  delayDuration?: number;
  /** ツールチップのトリガー要素のカーソルスタイル */
  triggerCursor?: string;
  /** ツールチップのコンテナスタイル */
  containerClassName?: string;
  /** 要素が省略されている場合のみツールチップを表示する */
  ellipsisOnly?: boolean;
};

/**
 * TooltipWrapperコンポーネント
 *
 * このコンポーネントは、ツールチップを表示するためのラッパーです。
 * 子要素とツールチップの内容を受け取り、それを表示します。
 * ツールチップのコンテンツは常にポータル内で表示されます。
 *
 * @example
 * ```tsx
 * <TooltipWrapper content="ツールチップの内容">
 *   <button>ホバーしてください</button>
 * </TooltipWrapper>
 * ```
 */
export const TooltipWrapper = ({
  children,
  content,
  contentClassName,
  side,
  sideOffset,
  delayDuration,
  triggerCursor = "inherit",
  containerClassName,
  ellipsisOnly = false,
}: TooltipWrapperProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [hasEllipsis, setHasEllipsis] = useState(false);
  const childrenRef = useRef<ReactNode>(children);

  useEffect(() => {
    if (!ellipsisOnly) return;

    const el = triggerRef.current;
    if (!el) return;

    const checkEllipsis = () => {
      const currentEl = triggerRef.current;
      if (currentEl) {
        // 内側の実際のテキスト要素を取得
        const textElement = currentEl.children[0] as HTMLElement;
        const targetElement = textElement || currentEl;

        const hasEllipsisContent =
          targetElement.scrollHeight > targetElement.clientHeight ||
          targetElement.scrollWidth > targetElement.clientWidth;
        setHasEllipsis(hasEllipsisContent);
      }
    };

    const childrenChanged = childrenRef.current !== children;
    childrenRef.current = children;

    if (childrenChanged) {
      requestAnimationFrame(checkEllipsis);
    } else {
      checkEllipsis();
    }

    // windowのresizeイベントを監視
    const handleWindowResize = () => {
      requestAnimationFrame(checkEllipsis);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [ellipsisOnly, children]);

  const triggerContent = (
    <div
      ref={triggerRef}
      className={cn("inline-block", containerClassName)}
      style={{ cursor: triggerCursor }}
    >
      {children}
    </div>
  );

  // contentが空の場合はツールチップを表示しない
  if (!content) {
    return triggerContent;
  }

  // ellipsisOnlyがtrueで、要素が省略されていない場合はツールチップを表示しない
  if (ellipsisOnly && !hasEllipsis) {
    return triggerContent;
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>{triggerContent}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            className={contentClassName}
            side={side}
            sideOffset={sideOffset}
          >
            {content}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};
